import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
    Users,
    DollarSign,
    Briefcase,
    Clock,
    Shield,
    Gift,
    Ban,
    BarChart3,
    FileText,
    ChevronLeft,
    ChevronRight,
    Search,
    RefreshCw,
    Crown,
    X,
    Mail,
    Calendar,
    Activity,
    Trash2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
    const navigate = useNavigate();
    const { getAuthHeaders, user } = useAuth();
    
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    
    // Data states
    const [platformMetrics, setPlatformMetrics] = useState(null);
    const [userMetrics, setUserMetrics] = useState([]);
    const [revenueMetrics, setRevenueMetrics] = useState(null);
    const [auditLogs, setAuditLogs] = useState([]);
    const [revenuePeriod, setRevenuePeriod] = useState('monthly');
    
    // UI states
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [showGrantDialog, setShowGrantDialog] = useState(false);
    const [showRevokeDialog, setShowRevokeDialog] = useState(false);
    const [showUserDetail, setShowUserDetail] = useState(false);
    const [showCleanupDialog, setShowCleanupDialog] = useState(false);
    const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [cleanupConfirmText, setCleanupConfirmText] = useState('');
    const [grantReason, setGrantReason] = useState('');
    const [revokeReason, setRevokeReason] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [cleanupResult, setCleanupResult] = useState(null);
    const [cleanupPreview, setCleanupPreview] = useState(null);
    const [excludedEmails, setExcludedEmails] = useState([]);

    // Check admin status
    useEffect(() => {
        const checkAdmin = async () => {
            try {
                const response = await axios.get(`${API}/api/admin/check`, {
                    headers: getAuthHeaders()
                });
                setIsAdmin(response.data.is_admin);
                if (!response.data.is_admin) {
                    toast.error('Access denied: Admin privileges required');
                    navigate('/');
                }
            } catch (error) {
                toast.error('Access denied');
                navigate('/');
            } finally {
                setLoading(false);
            }
        };
        checkAdmin();
    }, [getAuthHeaders, navigate]);

    // Fetch data
    const fetchData = useCallback(async () => {
        try {
            const [metricsRes, usersRes, revenueRes, auditRes] = await Promise.all([
                axios.get(`${API}/api/admin/platform-metrics`, { headers: getAuthHeaders() }),
                axios.get(`${API}/api/admin/users`, { headers: getAuthHeaders() }),
                axios.get(`${API}/api/admin/revenue?period=${revenuePeriod}`, { headers: getAuthHeaders() }),
                axios.get(`${API}/api/admin/audit-logs`, { headers: getAuthHeaders() })
            ]);
            setPlatformMetrics(metricsRes.data);
            // Map backend field names to frontend expected names
            const usersWithTier = (usersRes.data.users || []).map(u => ({
                ...u,
                tier: u.subscription_status === 'active' ? 'paid' 
                    : u.subscription_status === 'complimentary' ? 'complimentary' 
                    : 'free',
                total_cases: u.case_count || 0,
                total_hours: u.total_hours || 0,
                signup_date: u.created_at
            }));
            setUserMetrics(usersWithTier);
            setRevenueMetrics(revenueRes.data);
            setAuditLogs(auditRes.data.logs || []);
        } catch (error) {
            console.error('Failed to fetch admin data:', error);
        }
    }, [getAuthHeaders, revenuePeriod]);

    useEffect(() => {
        if (isAdmin) {
            fetchData();
        }
    }, [isAdmin, fetchData]);

    // Filter users by search
    const filteredUsers = userMetrics.filter(u => 
        u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Grant complimentary subscription
    const handleGrantComplimentary = async () => {
        if (!selectedUser || !grantReason.trim()) return;
        
        setIsProcessing(true);
        try {
            await axios.post(`${API}/api/admin/grant-complimentary`, {
                user_id: selectedUser.id,
                duration_days: 180,
                reason: grantReason
            }, { headers: getAuthHeaders() });
            
            toast.success('Complimentary subscription granted');
            setShowGrantDialog(false);
            setGrantReason('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to grant subscription');
        } finally {
            setIsProcessing(false);
        }
    };

    // Revoke complimentary subscription
    const handleRevokeComplimentary = async () => {
        if (!selectedUser || !revokeReason.trim()) return;
        
        setIsProcessing(true);
        try {
            await axios.post(`${API}/api/admin/revoke-complimentary`, {
                user_id: selectedUser.id,
                reason: revokeReason
            }, { headers: getAuthHeaders() });
            
            toast.success('Complimentary subscription revoked');
            setShowRevokeDialog(false);
            setRevokeReason('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to revoke subscription');
        } finally {
            setIsProcessing(false);
        }
    };

    // Preview cleanup (first step)
    const handleCleanupPreview = async () => {
        setIsProcessing(true);
        setCleanupPreview(null);
        setCleanupResult(null);
        try {
            const response = await axios.post(`${API}/api/admin/cleanup-test-accounts`, 
                { preview: true, excluded_emails: excludedEmails },
                { headers: getAuthHeaders() }
            );
            setCleanupPreview(response.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to preview cleanup');
        } finally {
            setIsProcessing(false);
        }
    };

    // Execute cleanup (after confirmation)
    const handleCleanupExecute = async () => {
        if (cleanupConfirmText !== 'CLEANUP') return;
        
        setIsProcessing(true);
        setCleanupResult(null);
        try {
            const response = await axios.post(`${API}/api/admin/cleanup-test-accounts`, 
                { preview: false, excluded_emails: excludedEmails },
                { headers: getAuthHeaders() }
            );
            
            setCleanupResult(response.data);
            setCleanupPreview(null);
            toast.success(response.data.message);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to cleanup test accounts');
        } finally {
            setIsProcessing(false);
        }
    };

    // Toggle email exclusion
    const toggleExcludeEmail = (email) => {
        setExcludedEmails(prev => 
            prev.includes(email) 
                ? prev.filter(e => e !== email)
                : [...prev, email]
        );
    };

    // Delete individual user
    const handleDeleteUser = async () => {
        if (!selectedUser || deleteConfirmText !== 'DELETE') return;
        
        setIsProcessing(true);
        try {
            const response = await axios.delete(`${API}/api/admin/users/${selectedUser.id}`, {
                headers: getAuthHeaders()
            });
            
            toast.success(response.data.message);
            setShowDeleteUserDialog(false);
            setShowUserDetail(false);
            setDeleteConfirmText('');
            setSelectedUser(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete user');
        } finally {
            setIsProcessing(false);
        }
    };

    // Get tier badge
    const getTierBadge = (tier) => {
        switch (tier) {
            case 'pro':
            case 'paid':
                return <Badge className="bg-emerald-100 text-emerald-700 border-0">Paid</Badge>;
            case 'complimentary':
                return <Badge className="bg-violet-100 text-violet-700 border-0">Complimentary</Badge>;
            default:
                return <Badge className="bg-slate-100 text-slate-600 border-0">Free</Badge>;
        }
    };

    // Tab button component for mobile-friendly tabs
    const TabButton = ({ value, icon: Icon, label }) => (
        <button
            onClick={() => setActiveTab(value)}
            className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex-1 min-w-0 ${
                activeTab === value
                    ? 'bg-violet-600 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid={`tab-${value}`}
        >
            <Icon className="h-4 w-4 flex-shrink-0" />
            <span className="hidden sm:inline">{label}</span>
        </button>
    );

    // User card for mobile view
    const UserCard = ({ u }) => (
        <div 
            className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 active:bg-slate-50 cursor-pointer"
            onClick={() => {
                setSelectedUser(u);
                setShowUserDetail(true);
            }}
            data-testid={`user-card-${u.user_id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{u.name || 'No name'}</p>
                    <p className="text-xs text-slate-500 truncate">{u.email}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                    {getTierBadge(u.tier)}
                    <Badge className={
                        u.subscription_status === 'active' 
                            ? 'bg-emerald-100 text-emerald-700 border-0 text-xs'
                            : u.subscription_status === 'expired'
                                ? 'bg-red-100 text-red-700 border-0 text-xs'
                                : 'bg-slate-100 text-slate-600 border-0 text-xs'
                    }>
                        {u.subscription_status}
                    </Badge>
                </div>
            </div>
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
                <div className="flex items-center gap-1.5 text-sm text-slate-600">
                    <Briefcase className="h-3.5 w-3.5" />
                    <span>{u.total_cases} cases</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-slate-600">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{u.total_hours?.toFixed(1)}h</span>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-400 ml-auto" />
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <RefreshCw className="h-8 w-8 animate-spin text-violet-600" />
            </div>
        );
    }

    if (!isAdmin) {
        return null;
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header - Mobile Optimized */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
                <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <button 
                                onClick={() => navigate('/')} 
                                className="p-2 hover:bg-white/10 rounded-full flex-shrink-0"
                                data-testid="back-btn"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </button>
                            <div className="min-w-0">
                                <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2 truncate">
                                    <Shield className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
                                    <span className="truncate">Admin</span>
                                </h1>
                                <p className="text-slate-300 text-xs sm:text-sm hidden sm:block">Platform Management</p>
                            </div>
                        </div>
                        <Button 
                            onClick={fetchData} 
                            variant="outline" 
                            size="sm"
                            className="border-white/20 text-white hover:bg-white/10 flex-shrink-0"
                        >
                            <RefreshCw className="h-4 w-4 sm:mr-2" />
                            <span className="hidden sm:inline">Refresh</span>
                        </Button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
                {/* Tabs - Mobile Optimized */}
                <div className="bg-white shadow-sm rounded-xl p-1.5 mb-4 sm:mb-6 flex gap-1">
                    <TabButton value="overview" icon={BarChart3} label="Overview" />
                    <TabButton value="users" icon={Users} label="Users" />
                    <TabButton value="revenue" icon={DollarSign} label="Revenue" />
                    <TabButton value="audit" icon={FileText} label="Audit" />
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="space-y-4 sm:space-y-6">
                        {/* Platform Metrics Cards */}
                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                            <Card className="border-0 shadow-soft">
                                <CardContent className="p-4 sm:pt-6">
                                    <div className="flex items-center gap-2 sm:gap-3">
                                        <div className="p-2 sm:p-3 bg-blue-100 rounded-xl flex-shrink-0">
                                            <Users className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xs sm:text-sm text-muted-foreground">Users</p>
                                            <p className="text-xl sm:text-2xl font-bold">{platformMetrics?.total_users || 0}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                            
                            <Card className="border-0 shadow-soft">
                                <CardContent className="p-4 sm:pt-6">
                                    <div className="flex items-center gap-2 sm:gap-3">
                                        <div className="p-2 sm:p-3 bg-emerald-100 rounded-xl flex-shrink-0">
                                            <Briefcase className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xs sm:text-sm text-muted-foreground">Cases</p>
                                            <p className="text-xl sm:text-2xl font-bold">{platformMetrics?.total_cases || 0}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                            
                            <Card className="border-0 shadow-soft">
                                <CardContent className="p-4 sm:pt-6">
                                    <div className="flex items-center gap-2 sm:gap-3">
                                        <div className="p-2 sm:p-3 bg-amber-100 rounded-xl flex-shrink-0">
                                            <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xs sm:text-sm text-muted-foreground">Hours</p>
                                            <p className="text-xl sm:text-2xl font-bold">{platformMetrics?.total_hours?.toFixed(0) || 0}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                            
                            <Card className="border-0 shadow-soft">
                                <CardContent className="p-4 sm:pt-6">
                                    <div className="flex items-center gap-2 sm:gap-3">
                                        <div className="p-2 sm:p-3 bg-violet-100 rounded-xl flex-shrink-0">
                                            <Crown className="h-5 w-5 sm:h-6 sm:w-6 text-violet-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xs sm:text-sm text-muted-foreground">Paid</p>
                                            <p className="text-xl sm:text-2xl font-bold">{platformMetrics?.users_by_tier?.paid || 0}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* User Distribution */}
                        <Card className="border-0 shadow-soft">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base sm:text-lg">User Distribution</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">Free</span>
                                        <div className="flex items-center gap-2">
                                            <div className="h-2 bg-slate-200 rounded-full w-24 sm:w-32">
                                                <div 
                                                    className="h-2 bg-slate-500 rounded-full" 
                                                    style={{ width: `${(platformMetrics?.free_users / platformMetrics?.total_users * 100) || 0}%` }}
                                                />
                                            </div>
                                            <span className="font-medium w-8 text-right text-sm">{platformMetrics?.free_users || 0}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">Paid</span>
                                        <div className="flex items-center gap-2">
                                            <div className="h-2 bg-slate-200 rounded-full w-24 sm:w-32">
                                                <div 
                                                    className="h-2 bg-emerald-500 rounded-full" 
                                                    style={{ width: `${(platformMetrics?.active_subscribers / platformMetrics?.total_users * 100) || 0}%` }}
                                                />
                                            </div>
                                            <span className="font-medium w-8 text-right text-sm">{platformMetrics?.active_subscribers || 0}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">Complimentary</span>
                                        <div className="flex items-center gap-2">
                                            <div className="h-2 bg-slate-200 rounded-full w-24 sm:w-32">
                                                <div 
                                                    className="h-2 bg-violet-500 rounded-full" 
                                                    style={{ width: `${(platformMetrics?.complimentary_users / platformMetrics?.total_users * 100) || 0}%` }}
                                                />
                                            </div>
                                            <span className="font-medium w-8 text-right text-sm">{platformMetrics?.complimentary_users || 0}</span>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                    <div className="space-y-4">
                        {/* Search and Cleanup */}
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search users..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-10 h-11 rounded-xl"
                                    data-testid="search-users"
                                />
                            </div>
                            <Button 
                                variant="outline" 
                                onClick={() => setShowCleanupDialog(true)}
                                className="h-11 rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                                data-testid="cleanup-btn"
                            >
                                <Ban className="h-4 w-4 mr-1" />
                                Cleanup
                            </Button>
                        </div>

                        {/* User List - Mobile Cards */}
                        <div className="space-y-3">
                            {filteredUsers.map(u => (
                                <UserCard key={u.user_id} u={u} />
                            ))}
                            {filteredUsers.length === 0 && (
                                <div className="text-center py-8 text-muted-foreground">
                                    No users found
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Revenue Tab */}
                {activeTab === 'revenue' && (
                    <div className="space-y-4">
                        {/* Period Selector */}
                        <Select value={revenuePeriod} onValueChange={setRevenuePeriod}>
                            <SelectTrigger className="w-full sm:w-40">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="daily">Daily</SelectItem>
                                <SelectItem value="monthly">Monthly</SelectItem>
                                <SelectItem value="yearly">Yearly</SelectItem>
                            </SelectContent>
                        </Select>

                        {/* Revenue Cards */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-emerald-50 rounded-xl p-4">
                                <p className="text-xs sm:text-sm text-emerald-600">Gross</p>
                                <p className="text-lg sm:text-2xl font-bold text-emerald-700">
                                    ${revenueMetrics?.gross_revenue?.toFixed(0) || '0'}
                                </p>
                            </div>
                            <div className="bg-blue-50 rounded-xl p-4">
                                <p className="text-xs sm:text-sm text-blue-600">Net</p>
                                <p className="text-lg sm:text-2xl font-bold text-blue-700">
                                    ${revenueMetrics?.net_revenue?.toFixed(0) || '0'}
                                </p>
                            </div>
                            <div className="bg-violet-50 rounded-xl p-4">
                                <p className="text-xs sm:text-sm text-violet-600">MRR</p>
                                <p className="text-lg sm:text-2xl font-bold text-violet-700">
                                    ${revenueMetrics?.mrr?.toFixed(0) || '0'}
                                </p>
                            </div>
                            <div className="bg-amber-50 rounded-xl p-4">
                                <p className="text-xs sm:text-sm text-amber-600">ARR</p>
                                <p className="text-lg sm:text-2xl font-bold text-amber-700">
                                    ${revenueMetrics?.arr?.toFixed(0) || '0'}
                                </p>
                            </div>
                        </div>

                        {/* Stats Card */}
                        <Card className="border-0 shadow-soft">
                            <CardContent className="p-4 space-y-3">
                                <div className="flex justify-between py-2 border-b">
                                    <span className="text-sm text-muted-foreground">Active Subscribers</span>
                                    <span className="font-medium">{revenueMetrics?.active_paid_subscribers || 0}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b">
                                    <span className="text-sm text-muted-foreground">New Subscriptions</span>
                                    <span className="font-medium text-emerald-600">+{revenueMetrics?.new_subscriptions || 0}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b">
                                    <span className="text-sm text-muted-foreground">Cancellations</span>
                                    <span className="font-medium text-red-600">-{revenueMetrics?.cancellations || 0}</span>
                                </div>
                                <div className="flex justify-between py-2">
                                    <span className="text-sm text-muted-foreground">Churn Rate</span>
                                    <span className="font-medium">{revenueMetrics?.churn_rate?.toFixed(1) || 0}%</span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Audit Log Tab */}
                {activeTab === 'audit' && (
                    <div className="space-y-3">
                        {auditLogs.map(log => (
                            <Card 
                                key={log.id} 
                                className="border-0 shadow-soft cursor-pointer hover:shadow-md transition-shadow active:bg-slate-50"
                                onClick={() => {
                                    // Find the target user from userMetrics
                                    const targetUser = userMetrics.find(u => u.email === log.target_user_email);
                                    if (targetUser) {
                                        setSelectedUser(targetUser);
                                        setShowUserDetail(true);
                                    } else {
                                        toast.error('User not found');
                                    }
                                }}
                            >
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <Badge className={
                                                    log.action === 'grant_complimentary' 
                                                        ? 'bg-violet-100 text-violet-700 border-0 text-xs'
                                                        : log.action === 'revoke_complimentary'
                                                            ? 'bg-red-100 text-red-700 border-0 text-xs'
                                                            : 'bg-slate-100 text-slate-600 border-0 text-xs'
                                                }>
                                                    {log.action.replace(/_/g, ' ')}
                                                </Badge>
                                            </div>
                                            <p className="text-sm font-medium truncate">{log.target_user_email}</p>
                                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                                {log.reason || (typeof log.details === 'string' ? log.details : '')}
                                            </p>
                                        </div>
                                        <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
                                            <p className="text-xs text-muted-foreground">
                                                {log.timestamp ? format(new Date(log.timestamp), 'MMM d') : '-'}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {log.timestamp ? format(new Date(log.timestamp), 'HH:mm') : ''}
                                            </p>
                                            <ChevronRight className="h-4 w-4 text-slate-400 mt-1" />
                                        </div>
                                    </div>
                                    <p className="text-xs text-slate-400 mt-2">by {log.admin_email}</p>
                                </CardContent>
                            </Card>
                        ))}
                        {auditLogs.length === 0 && (
                            <div className="text-center py-8 text-muted-foreground">
                                No audit log entries yet
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* User Detail Modal */}
            <Dialog open={showUserDetail} onOpenChange={setShowUserDetail}>
                <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center justify-between">
                            <span>User Details</span>
                        </DialogTitle>
                    </DialogHeader>
                    {selectedUser && (
                        <div className="space-y-4">
                            {/* User Info */}
                            <div className="space-y-3">
                                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                                    <div className="p-2 bg-slate-200 rounded-full">
                                        <Users className="h-5 w-5 text-slate-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium truncate">{selectedUser.name || 'No name'}</p>
                                        <p className="text-sm text-muted-foreground truncate">{selectedUser.email}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-3 bg-slate-50 rounded-xl">
                                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                                            <Crown className="h-4 w-4" />
                                            <span className="text-xs">Tier</span>
                                        </div>
                                        {getTierBadge(selectedUser.tier)}
                                    </div>
                                    <div className="p-3 bg-slate-50 rounded-xl">
                                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                                            <Activity className="h-4 w-4" />
                                            <span className="text-xs">Status</span>
                                        </div>
                                        <Badge className={
                                            selectedUser.subscription_status === 'active' 
                                                ? 'bg-emerald-100 text-emerald-700 border-0'
                                                : selectedUser.subscription_status === 'expired'
                                                    ? 'bg-red-100 text-red-700 border-0'
                                                    : 'bg-slate-100 text-slate-600 border-0'
                                        }>
                                            {selectedUser.subscription_status}
                                        </Badge>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-3 bg-slate-50 rounded-xl">
                                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                                            <Briefcase className="h-4 w-4" />
                                            <span className="text-xs">Cases</span>
                                        </div>
                                        <p className="text-lg font-bold">{selectedUser.total_cases}</p>
                                    </div>
                                    <div className="p-3 bg-slate-50 rounded-xl">
                                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                                            <Clock className="h-4 w-4" />
                                            <span className="text-xs">Hours</span>
                                        </div>
                                        <p className="text-lg font-bold">{selectedUser.total_hours?.toFixed(1)}</p>
                                    </div>
                                </div>

                                <div className="p-3 bg-slate-50 rounded-xl">
                                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                                        <Calendar className="h-4 w-4" />
                                        <span className="text-xs">Joined</span>
                                    </div>
                                    <p className="font-medium">
                                        {selectedUser.signup_date 
                                            ? format(new Date(selectedUser.signup_date), 'MMMM d, yyyy')
                                            : 'Unknown'
                                        }
                                    </p>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="space-y-3 pt-2">
                                <div className="flex gap-3">
                                    {selectedUser.tier === 'free' && (
                                        <Button
                                            onClick={() => {
                                                setShowUserDetail(false);
                                                setShowGrantDialog(true);
                                            }}
                                            className="flex-1 bg-violet-600 hover:bg-violet-700"
                                        >
                                            <Gift className="h-4 w-4 mr-2" />
                                            Grant Complimentary
                                        </Button>
                                    )}
                                    {selectedUser.tier === 'complimentary' && (
                                        <Button
                                            variant="destructive"
                                            onClick={() => {
                                                setShowUserDetail(false);
                                                setShowRevokeDialog(true);
                                            }}
                                            className="flex-1"
                                        >
                                            <Ban className="h-4 w-4 mr-2" />
                                            Revoke Subscription
                                        </Button>
                                    )}
                                    <Button
                                        variant="outline"
                                        onClick={() => setShowUserDetail(false)}
                                        className={selectedUser.tier === 'paid' ? 'flex-1' : ''}
                                    >
                                        Close
                                    </Button>
                                </div>
                                
                                {/* Delete User Button */}
                                <Button
                                    variant="ghost"
                                    onClick={() => {
                                        setShowUserDetail(false);
                                        setShowDeleteUserDialog(true);
                                    }}
                                    className="w-full text-red-500 hover:text-red-600 hover:bg-red-50"
                                >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete User
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Grant Complimentary Dialog */}
            <Dialog open={showGrantDialog} onOpenChange={setShowGrantDialog}>
                <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Gift className="h-5 w-5 text-violet-600" />
                            Grant Complimentary
                        </DialogTitle>
                        <DialogDescription>
                            6-month free subscription for {selectedUser?.email}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Reason (required)</Label>
                            <Textarea
                                value={grantReason}
                                onChange={(e) => setGrantReason(e.target.value)}
                                placeholder="Enter reason..."
                                rows={3}
                            />
                        </div>
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowGrantDialog(false);
                                    setGrantReason('');
                                }}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleGrantComplimentary}
                                disabled={isProcessing || !grantReason.trim()}
                                className="flex-1 bg-violet-600 hover:bg-violet-700"
                            >
                                {isProcessing ? 'Processing...' : 'Grant'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Revoke Complimentary Dialog */}
            <Dialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
                <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <Ban className="h-5 w-5" />
                            Revoke Subscription
                        </DialogTitle>
                        <DialogDescription>
                            Remove complimentary access for {selectedUser?.email}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Reason (required)</Label>
                            <Textarea
                                value={revokeReason}
                                onChange={(e) => setRevokeReason(e.target.value)}
                                placeholder="Enter reason..."
                                rows={3}
                            />
                        </div>
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowRevokeDialog(false);
                                    setRevokeReason('');
                                }}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleRevokeComplimentary}
                                disabled={isProcessing || !revokeReason.trim()}
                                className="flex-1"
                            >
                                {isProcessing ? 'Processing...' : 'Revoke'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Individual User Dialog */}
            <Dialog open={showDeleteUserDialog} onOpenChange={(open) => {
                setShowDeleteUserDialog(open);
                if (!open) setDeleteConfirmText('');
            }}>
                <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <Trash2 className="h-5 w-5" />
                            Delete User
                        </DialogTitle>
                        <DialogDescription>
                            This will permanently delete this user and all their data.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedUser && (
                        <div className="space-y-4 py-4">
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                <p className="text-sm text-red-800 font-medium mb-2">
                                    You are about to delete:
                                </p>
                                <p className="text-sm text-red-700">
                                    <strong>{selectedUser.name || 'Unknown'}</strong>
                                    <br />
                                    {selectedUser.email}
                                </p>
                                <p className="text-xs text-red-600 mt-2">
                                    This will also delete {selectedUser.total_cases || 0} cases, {selectedUser.total_hours?.toFixed(1) || 0} hours of time entries, and all associated data.
                                </p>
                            </div>
                            
                            <div className="space-y-2">
                                <p className="text-sm font-medium">
                                    Type <span className="font-mono bg-slate-100 px-1 rounded">DELETE</span> to confirm:
                                </p>
                                <Input
                                    value={deleteConfirmText}
                                    onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                                    placeholder="Type DELETE"
                                    className="h-11 rounded-xl font-mono"
                                    data-testid="delete-confirm-input"
                                />
                            </div>
                            
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setShowDeleteUserDialog(false);
                                        setDeleteConfirmText('');
                                    }}
                                    className="flex-1 rounded-xl"
                                    disabled={isProcessing}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleDeleteUser}
                                    disabled={isProcessing || deleteConfirmText !== 'DELETE'}
                                    className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 disabled:bg-red-300"
                                >
                                    {isProcessing ? (
                                        <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
                                    ) : (
                                        <><Trash2 className="h-4 w-4 mr-2" />Delete User</>
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Cleanup Test Accounts Dialog */}
            <Dialog open={showCleanupDialog} onOpenChange={(open) => {
                setShowCleanupDialog(open);
                if (!open) {
                    setCleanupPreview(null);
                    setCleanupResult(null);
                    setCleanupConfirmText('');
                    setExcludedEmails([]);
                }
            }}>
                <DialogContent className="w-[calc(100%-2rem)] max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Ban className="h-5 w-5 text-red-500" />
                            Cleanup Test Accounts
                        </DialogTitle>
                        <DialogDescription>
                            Delete user accounts without passwords (test accounts) and their data.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {/* Step 1: Preview */}
                        {!cleanupPreview && !cleanupResult && (
                            <>
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                    <p className="text-sm text-amber-800">
                                        <strong>Step 1:</strong> First, preview which accounts will be deleted. You can then exclude specific accounts before confirming.
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        onClick={() => setShowCleanupDialog(false)}
                                        className="flex-1 rounded-xl"
                                        disabled={isProcessing}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleCleanupPreview}
                                        disabled={isProcessing}
                                        className="flex-1 rounded-xl bg-amber-600 hover:bg-amber-700"
                                    >
                                        {isProcessing ? (
                                            <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Loading...</>
                                        ) : (
                                            <><Search className="h-4 w-4 mr-2" />Preview Cleanup</>
                                        )}
                                    </Button>
                                </div>
                            </>
                        )}

                        {/* Step 2: Review and Exclude */}
                        {cleanupPreview && !cleanupResult && (
                            <>
                                <div className="bg-slate-50 border rounded-xl p-4">
                                    <p className="text-sm font-medium text-slate-700 mb-2">
                                        Found {cleanupPreview.would_delete_count} test accounts to delete:
                                    </p>
                                    <div className="max-h-40 overflow-y-auto space-y-2">
                                        {cleanupPreview.would_delete?.map((email, i) => (
                                            <div key={i} className="flex items-center justify-between text-sm p-2 bg-white rounded-lg border">
                                                <span className={excludedEmails.includes(email) ? 'line-through text-slate-400' : 'text-slate-700'}>
                                                    {email}
                                                </span>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => toggleExcludeEmail(email)}
                                                    className={`h-7 px-2 ${excludedEmails.includes(email) ? 'text-emerald-600' : 'text-red-500'}`}
                                                >
                                                    {excludedEmails.includes(email) ? 'Keep' : 'Exclude'}
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                    {cleanupPreview.would_delete_count === 0 && (
                                        <p className="text-sm text-slate-500 italic">No test accounts found.</p>
                                    )}
                                </div>

                                {excludedEmails.length > 0 && (
                                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                                        <p className="text-sm text-emerald-700">
                                            <strong>{excludedEmails.length}</strong> account(s) will be kept (excluded from cleanup)
                                        </p>
                                    </div>
                                )}

                                <div className="bg-slate-50 rounded-xl p-3">
                                    <p className="text-xs text-slate-600">
                                        <strong>{cleanupPreview.kept_count}</strong> users with passwords are protected and won't be deleted.
                                    </p>
                                </div>

                                {(cleanupPreview.would_delete_count - excludedEmails.length) > 0 && (
                                    <div className="space-y-2 border-t pt-4">
                                        <p className="text-sm font-medium text-red-600">
                                            Type <span className="font-mono bg-red-100 px-1 rounded">CLEANUP</span> to confirm deletion of {cleanupPreview.would_delete_count - excludedEmails.length} account(s):
                                        </p>
                                        <Input
                                            value={cleanupConfirmText}
                                            onChange={(e) => setCleanupConfirmText(e.target.value.toUpperCase())}
                                            placeholder="Type CLEANUP"
                                            className="h-11 rounded-xl font-mono"
                                        />
                                    </div>
                                )}

                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setCleanupPreview(null);
                                            setCleanupConfirmText('');
                                        }}
                                        className="flex-1 rounded-xl"
                                        disabled={isProcessing}
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        onClick={handleCleanupExecute}
                                        disabled={isProcessing || cleanupConfirmText !== 'CLEANUP' || (cleanupPreview.would_delete_count - excludedEmails.length) === 0}
                                        className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 disabled:bg-red-300"
                                    >
                                        {isProcessing ? (
                                            <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
                                        ) : (
                                            <><Ban className="h-4 w-4 mr-2" />Delete {cleanupPreview.would_delete_count - excludedEmails.length} Accounts</>
                                        )}
                                    </Button>
                                </div>
                            </>
                        )}

                        {/* Step 3: Results */}
                        {cleanupResult && (
                            <>
                                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                                    <p className="text-sm font-medium text-emerald-800 mb-2">
                                        ✓ {cleanupResult.message}
                                    </p>
                                    {cleanupResult.deleted_count > 0 && (
                                        <div className="text-sm text-emerald-700 space-y-1">
                                            <p>Deleted users: {cleanupResult.deleted_count}</p>
                                            {cleanupResult.cleanup_stats && (
                                                <>
                                                    <p>Cases removed: {cleanupResult.cleanup_stats.cases_deleted || 0}</p>
                                                    <p>Time entries removed: {cleanupResult.cleanup_stats.time_entries_deleted || 0}</p>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="bg-slate-50 rounded-xl p-4">
                                    <p className="text-sm font-medium text-slate-700 mb-2">
                                        Kept {cleanupResult.kept_count} users:
                                    </p>
                                    <div className="text-sm text-slate-600 space-y-1 max-h-32 overflow-y-auto">
                                        {cleanupResult.kept_users?.map((email, i) => (
                                            <p key={i}>• {email}</p>
                                        ))}
                                    </div>
                                </div>
                                <Button
                                    onClick={() => {
                                        setShowCleanupDialog(false);
                                        setCleanupResult(null);
                                        setCleanupPreview(null);
                                        setCleanupConfirmText('');
                                        setExcludedEmails([]);
                                    }}
                                    className="w-full rounded-xl"
                                >
                                    Done
                                </Button>
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
